
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCountry } from "@/contexts/country";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import CountryPollsTab from "@/components/polls/CountryPollsTab";
import CountryHero from "@/components/country/CountryHero";
import CountryFeaturedVideo from "@/components/country/CountryFeaturedVideo";
import InsightsCTA from "@/components/insights/InsightsCTA";
import SuggestPollModal from "@/components/polls/SuggestPollModal";
import { Button } from "@/components/ui/button";
import { LightbulbIcon } from "lucide-react";
import CountrySocialLinks from "@/components/country/CountrySocialLinks";

// Import Cayman Enhanced sections 
import HowItWorksSection from "@/components/cayman/sections/HowItWorksSection";
import NationalSentimentDashboard from "@/components/cayman/sections/NationalSentimentDashboard";
import FeaturedInsightsCarousel from "@/components/cayman/sections/FeaturedInsightsCarousel";
import SolutionActionSpotlight from "@/components/cayman/sections/SolutionActionSpotlight";
import JoinMovementSection from "@/components/cayman/sections/JoinMovementSection";
import ResourcesGrid from "@/components/cayman/sections/ResourcesGrid";
import UnderstandingCards from "@/components/cayman/sections/UnderstandingCards";

const CountryCards = () => {
  const { countryCode } = useParams();
  const navigate = useNavigate();
  const { availableCountries, setCountry, countryName, isLoading, selectedCountry } = useCountry();
  const [showSuggestPoll, setShowSuggestPoll] = useState(false);
  
  useEffect(() => {
    if (countryCode && !isLoading) {
      const isValidCountry = availableCountries.some(
        (country) => country.code === countryCode && country.active
      );

      if (isValidCountry) {
        setCountry(countryCode);
      } else {
        toast.error("Country not found or not available");
        navigate("/countries");
      }
    }
  }, [countryCode, availableCountries, setCountry, navigate, isLoading]);

  const currentCountry = availableCountries.find(c => c.code === countryCode);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 pt-24">
        <CountryHero 
          countryName={countryName || "Country"}
          backgroundImageUrl={currentCountry?.background_image_url}
          backgroundImageAlt={currentCountry?.background_image_alt}
        />

        {/* Featured Video - New section placed after the hero but before polls section */}
        {selectedCountry?.featured_video && selectedCountry.featured_video.enabled && (
          <CountryFeaturedVideo videoConfig={selectedCountry.featured_video} />
        )}

        {/* Polls section */}
        <section className="py-10 bg-gray-50 dark:bg-gray-800/50">
          <div className="container mx-auto px-4">
            <CountryPollsTab countryName={countryName} />
            
            {/* Poll suggestion button */}
            <div className="text-center mt-4">
              <Button 
                onClick={() => setShowSuggestPoll(true)}
                variant="outline"
                className="gap-2"
              >
                <LightbulbIcon size={16} />
                Suggest a Poll
              </Button>
            </div>
          </div>
        </section>

        {/* Integrate Cayman Enhanced sections */}
        <NationalSentimentDashboard />
        <UnderstandingCards />
        <HowItWorksSection />
        <FeaturedInsightsCarousel />
        <SolutionActionSpotlight />
        
        {/* Join the Movement Section */}
        <JoinMovementSection />
        
        {/* Resources Grid */}
        <ResourcesGrid />

        {/* Social Media Section */}
        {selectedCountry?.social_media && selectedCountry.social_media.length > 0 && (
          <CountrySocialLinks 
            countryName={countryName || "this country"}
            socialPlatforms={selectedCountry.social_media}
          />
        )}

        {/* Call to action */}
        <InsightsCTA countryName={countryName || "this country"} />
      </main>
      
      {/* Poll suggestion modal */}
      <SuggestPollModal 
        open={showSuggestPoll} 
        onClose={() => setShowSuggestPoll(false)} 
      />
      
      <Footer />
      <BackToTop />
    </div>
  );
};

export default CountryCards;
